import { get, orderBy } from 'lodash';
import { createSelector } from 'reselect';
import { ResourceType } from '../../../common/PricePrgClient';
import { RootState } from '../../../store';
import { createAdminPermission, includes, roundTwoDecimals, startsWith, toLowerCase } from '../../../utils';
import { getPricelists } from '../../pricelist/redux/pricelistSelectors';
import { getProducts } from '../../product/redux/productSelectors';
import { getStocks } from '../../stock/redux/stockSelectors';
import { getStockAmount } from '../../stock/stockFunctions';
import { ProductStock } from '../../stock/stockTypes';
import { getCurrentUserPermissions } from '../../user/redux/userSelectors';
import { dayAmount, PricePeriod, PricePlanRow } from '../basePriceTypes';

export const getPricePlans = (state: RootState) => state.basePrice.pricePlans;
export const getBasePriceTable = (state: RootState) => state.basePrice.ui.basePriceTable;
export const getShowAllArticles = (state: RootState) => state.basePrice.ui.showAllArticles;
export const getShowActivePricelist = (state: RootState) => state.basePrice.ui.showActivePricelist;
export const getSelectedPricelistId = (state: RootState) => state.basePrice.ui.selectedPricelistId;
export const getBasePriceTableVisibleColumnKeys = createSelector(
    [(state: RootState) => state.basePrice.ui.basePriceTable.columns],
    columns => columns.filter(x => !x.hide)
);
export const getIsLoadingBasePrices = (state: RootState) => state.basePrice.ui.isLoading;

export const getBasePriceSelectedWarehouseDate = (state: RootState) => state.basePrice.selectedWarehouseDate;

export const getBasePricePlanRows = createSelector(
    [
        getPricePlans,
        getProducts,
        getBasePriceTable,
        getShowAllArticles,
        getBasePriceSelectedWarehouseDate,
        getStocks
    ],
    (pricePlans, products, { filters, sorting }, showAllArticles, warehouseDate, stocks) => {
        const findPrice = (idx: number, basePricePeriods: PricePeriod[]) =>
            basePricePeriods.find(x => x.dayIndexFrom <= idx && x.dayIndexTo >= idx);

        let productStocks: ProductStock = {};
        if (warehouseDate.date != null && warehouseDate.warehouse != null) {
            productStocks = stocks[warehouseDate.warehouse] ?? {};
        }

        function getStock(productId: number) {
            const productStockGrp = productStocks[productId];
            if (productStockGrp == null) {
                return null;
            }
            return getStockAmount(warehouseDate.date, productStockGrp);
        }

        const dayIdxs = Array.from(Array(dayAmount).keys());

        const isFilteringPrices = dayIdxs.map(id => filters[id]).some(f => f != null && f !== '');

        const rows = Object.values(products)
            .map(p => [p, getStock(p.productId)] as const)
            .filter(
                ([p, stock]) =>
                    (!warehouseDate.warehouse || p.warehouseIds?.includes(warehouseDate.warehouse ?? 0)) &&
                    startsWith(filters.productId, p.productId.toString()) &&
                    includes(filters.productName, p.name) &&
                    includes(filters.customProductAttribute1, p.customAttribute1) &&
                    includes(filters.country, p.countryOfOrigin) &&
                    includes(filters.priceUnit, p.priceUnit) &&
                    includes(filters.purchaser, p.purchaser) &&
                    startsWith(filters.stock, stock?.toString()) &&
                    startsWith(filters.grossWeight, p.grossWeight?.toString()) &&
                    startsWith(filters.netWeight, p.netWeight?.toString())
            )
            .map(([product, stock]) => {
                const bppRow: PricePlanRow = {
                    productId: product.productId,
                    productName: product.name,
                    customProductAttribute1: product.customAttribute1 ?? '',
                    country: product.countryOfOrigin ?? '',
                    priceUnit: product.priceUnit ?? '',
                    purchaser: product.purchaser,
                    stock: stock ?? null,
                    grossWeight: product.grossWeight,
                    netWeight: product.netWeight,
                    priceChangeDirections: {},
                    updatedBys: {},
                    updatedDates: {},
                    saves: {
                        showSaving: {}
                    },
                    boxesPerPallet: product.boxesPerPallet ?? 0
                };

                const bpp = pricePlans[product.productId];

                let saveIndicatorSet = false;
                if (bpp != null) {
                    for (const dayIdx of dayIdxs) {
                        const price = findPrice(dayIdx, bpp.pricePeriods);

                        bppRow[dayIdx] = roundTwoDecimals(price?.price);
                        bppRow.priceChangeDirections[dayIdx] = price?.priceChangeIndicator;
                        if (price?.updatedBy != null) {
                            bppRow.updatedBys[dayIdx] = price.updatedBy;
                        }
                        if (price?.updatedDate != null) {
                            bppRow.updatedDates[dayIdx] = new Date(price.updatedDate);
                        }
                        if (price?.priceToBeSaved != null) {
                            if (!saveIndicatorSet) {
                                bppRow.saves.showSaving[dayIdx] = true;
                                saveIndicatorSet = true;
                            }

                            bppRow.saves[dayIdx] = price.priceToBeSaved;
                        }
                    }
                }

                return bppRow;
            })
            .filter(bppRow => {
                if (showAllArticles && !isFilteringPrices) {
                    return true;
                }

                if (!isFilteringPrices) {
                    return dayIdxs.map(id => bppRow[id]).some(pr => pr != null);
                }

                return dayIdxs
                    .map(id => [filters[id] as string, bppRow[id]] as const)
                    .every(([filter, price]) => startsWith(filter, price?.toString()));
            });

        const nullOrderPlacement = sorting.direction === 'asc' ? Infinity : -Infinity;

        const col = !isNaN(Number(sorting.column)) ? `updatedDates.${sorting.column}` : sorting.column;

        return orderBy(rows, [o => toLowerCase(get(o, col)) || nullOrderPlacement], sorting.direction);
    }
);

export const getSelectedBasePricelistPermission = createSelector(
    [getSelectedPricelistId, getCurrentUserPermissions],
    (selectedPricelistId, currentUserPermissions) => {
        if (selectedPricelistId == null) {
            return null;
        }

        if (currentUserPermissions.isAdmin) {
            return createAdminPermission(ResourceType.Pricelist, selectedPricelistId);
        }

        const pricelistPermissions = currentUserPermissions.recordPermissions.find(
            x => x.resourceType === ResourceType.Pricelist
        );

        if (pricelistPermissions == null) {
            return null;
        }

        return pricelistPermissions.recordPermissions.find(x => x.recordId === selectedPricelistId) ?? null;
    }
);

export const getSelectedBasePricelist = createSelector(
    [getSelectedPricelistId, getPricelists],
    (selectedPricelistId, pricelists) => {
        if (selectedPricelistId == null) {
            return null;
        }

        return pricelists[selectedPricelistId];
    }
);
